import React, { useState, useEffect } from 'react';
import api from '../../../services/api'; 
import { Container, Row, Col, Card, Button, Accordion, Tabs, Tab, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  FaCloud,
  FaServer,
  FaDatabase,
  FaShieldAlt,
  FaNetworkWired,
  FaCheckCircle,
  FaRocket,
  FaPhone,
  FaExternalLinkAlt,
  FaCogs,
  FaHdd,
  FaCube,
  FaBolt,
  FaClock,
  FaMoneyBillWave,
  FaGlobe,
  FaTools,
  FaSitemap,
  FaChartLine,
  FaArrowRight
} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { useLanguage } from '../../../contexts/LanguageContext'; // Import du hook de langue
import { Helmet } from 'react-helmet-async';

// Variants d'animation
const animationVariants = {
  fadeInUp: {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8 }
  },
  scaleIn: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.6 }
  }
};

const CloudComputing = () => {
  const { t } = useLanguage(); // Utilisation du contexte de langue
  
  // Hooks d'animation
  const [setHeroRef, heroInView] = useScrollAnimation(0.1);
  const [setCategoriesRef, categoriesInView] = useScrollAnimation(0.1);

  // ✅ LOGIQUE DYNAMIQUE : Récupération des produits depuis PostgreSQL
  const [dbProducts, setDbProducts] = useState([]);

  useEffect(() => {
    const fetchCloudProducts = async () => {
      try {
        const response = await api.get('/public/products?keyword=Cloud');
        setDbProducts(response.data);
      } catch (error) {
        console.error("Erreur API Cloud:", error);
      }
    };
    fetchCloudProducts();
  }, []);

  // Ancrage vers l'aperçu
  const scrollToOverview = () => {
    document.getElementById('cloud-overview')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>

      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.cloud.seo.title}</title>
        <meta name="description" content={t.cloud.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/cloud" />
        
        {/* Balises pour LinkedIn / Twitter (Social) */}
        <meta property="og:title" content={t.cloud.seo.title} />
        <meta property="og:description" content={t.cloud.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/cloud/cloud-hero.jpg" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* ---------------------------------- */}

      {/* 1️⃣ SECTION HERO (STATIQUE) */}
      <section
        ref={setHeroRef}
        className="hero-section-sii"
        style={{
          background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/cloud/cloud-hero.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {} }
                transition={{ duration: 0.8 }}
              >
                <motion.div {...animationVariants.scaleIn} transition={{ delay: 0.2 }} className="mb-4">
                  <span className="badge-sii">
                    <FaCloud className="me-2" />
                    {t.cloud.hero.badge}
                  </span>
                </motion.div>

                <motion.h1
                  className="display-3 fw-bold mb-4 text-white"
                  initial={{ opacity: 0, x: -50 }}
                  animate={heroInView ? { opacity: 1, x: 0 } : {} }
                  transition={{ duration: 0.8, delay: 0.3 }}
                  style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: '1.1', fontWeight: 700 }}
                  dangerouslySetInnerHTML={{ __html: t.cloud.hero.title }}
                />

                <motion.p
                  className="lead text-light mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {} }
                  transition={{ duration: 0.8, delay: 0.5 }}
                  style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)', maxWidth: 600, lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}
                >
                  {t.cloud.hero.subtitle}
                </motion.p>

                <motion.div
                  className="d-flex flex-wrap gap-3 mb-5"
                  initial={{ opacity: 0, y: 30 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {} }
                  transition={{ duration: 0.8, delay: 0.7 }}
                >
                  <Button
                    size="lg"
                    onClick={scrollToOverview}
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      background: '#d90000',
                      color: 'white',
                      border: 'none',
                      boxShadow: '0 4px 16px rgba(217, 0, 0, 0.3)'
                    }}
                  >
                    <FaRocket className="me-2" /> {t.cloud.hero.btnExplore}
                  </Button>
                  <Button
                    variant="outline-light"
                    size="lg"
                    href="tel:+21670839470"
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderWidth: '2px',
                      background: 'rgba(255,255,255,0.1)'
                    }}
                  >
                    <FaPhone className="me-2" /> {t.cloud.hero.btnContact}
                  </Button>
                </motion.div>
              </motion.div>
            </Col>

            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={heroInView ? { opacity: 1, x: 0 } : {} }
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-center"
              >
                <div style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '24px',
                  padding: '3rem',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <h3 className="text-white mb-4 fw-bold">{t.cloud.hero.whyTitle}</h3>
                  <div className="text-start">
                    {t.cloud.hero.whyList.map((feature, index) => (
                      <motion.div key={index} className="d-flex align-items-center mb-3" initial={{ opacity: 0, x: -20 }} animate={heroInView ? { opacity: 1, x: 0 } : {} } transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}>
                        <FaCheckCircle className="me-3 text-success" style={{ minWidth: 20 }} />
                        <span className="text-white">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 2️⃣ SECTION STATIQUE : APERÇU (ONGLETS) */}
      <section id="cloud-overview" className="cloud-overview-section py-5" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <Container>
          <motion.div
            ref={setCategoriesRef}
            variants={animationVariants.fadeInUp}
            initial="initial"
            animate={categoriesInView ? 'animate' : 'initial'}
            className="text-center mb-4"
          >
            <h2 className="fw-bold mb-2" style={{ fontSize: '2.2rem' }} dangerouslySetInnerHTML={{ __html: t.cloud.overview.title }} />
            <p className="text-muted" style={{ maxWidth: 820, margin: '0 auto' }}>
              {t.cloud.overview.subtitle}
            </p>
          </motion.div>

          <Tabs defaultActiveKey="apercu" id="cloud-tabs" className="mb-3" justify>
            <Tab eventKey="apercu" title={t.cloud.overview.tabs.apercu}>
              <div className="pt-3">
                <Row className="g-4 mb-4">
                  <Col lg={4} md={6}>
                    <Card className="h-100 border-0 shadow-sm" style={{ borderTop: '3px solid #d90000', borderRadius: 18 }}>
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="me-3" style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(217,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaServer style={{ color: '#d90000' }} />
                          </div>
                          <h5 className="fw-bold mb-0">{t.cloud.overview.cardSvc.title}</h5>
                        </div>
                        <ul className="list-unstyled mb-0">
                          {t.cloud.overview.cardSvc.list.map((item, idx) => (
                            <li key={`svc-${idx}`} className="d-flex align-items-start mb-2">
                              <FaCheckCircle className="text-success me-2 mt-1" size={12} />
                              <div>
                                <div className="fw-semibold">{item.t}</div>
                                <small className="text-muted">{item.d}</small>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={4} md={6}>
                    <Card className="h-100 border-0 shadow-sm" style={{ borderTop: '3px solid #d90000', borderRadius: 18 }}>
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="me-3" style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(217,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaCloud style={{ color: '#d90000' }} />
                          </div>
                          <h5 className="fw-bold mb-0">{t.cloud.overview.cardDep.title}</h5>
                        </div>
                        <ul className="list-unstyled mb-0">
                          {t.cloud.overview.cardDep.list.map((item, idx) => (
                            <li key={`dep-${idx}`} className="d-flex align-items-start mb-2">
                              <FaCheckCircle className="text-success me-2 mt-1" size={12} />
                              <div>
                                <div className="fw-semibold">{item.t}</div>
                                <small className="text-muted">{item.d}</small>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={4} md={12}>
                    <Card className="h-100 border-0 shadow-sm" style={{ borderTop: '3px solid #d90000', borderRadius: 18 }}>
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="me-3" style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(217,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaShieldAlt style={{ color: '#d90000' }} />
                          </div>
                          <h5 className="fw-bold mb-0">{t.cloud.overview.cardBen.title}</h5>
                        </div>
                        <div className="row g-2">
                          {/* Les icônes restent fixes, les textes sont traduits */}
                          <div className="col-12 d-flex align-items-center">
                            <div className="me-2" style={{ display: 'flex', alignItems: 'center' }}><FaCogs className="text-danger" /></div>
                            <small className="fw-semibold">{t.cloud.overview.cardBen.list[0]}</small>
                          </div>
                          <div className="col-12 d-flex align-items-center">
                            <div className="me-2" style={{ display: 'flex', alignItems: 'center' }}><FaHdd className="text-danger" /></div>
                            <small className="fw-semibold">{t.cloud.overview.cardBen.list[1]}</small>
                          </div>
                          <div className="col-12 d-flex align-items-center">
                            <div className="me-2" style={{ display: 'flex', alignItems: 'center' }}><FaNetworkWired className="text-danger" /></div>
                            <small className="fw-semibold">{t.cloud.overview.cardBen.list[2]}</small>
                          </div>
                          <div className="col-12 d-flex align-items-center">
                            <div className="me-2" style={{ display: 'flex', alignItems: 'center' }}><FaServer className="text-danger" /></div>
                            <small className="fw-semibold">{t.cloud.overview.cardBen.list[3]}</small>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            </Tab>
            <Tab eventKey="architecture" title={t.cloud.overview.tabs.architecture}>
                <div className="p-4 bg-white rounded shadow-sm mt-3">
                    <FaSitemap className="text-danger me-2" /> <strong>{t.cloud.overview.archText.split(':')[0]} :</strong> {t.cloud.overview.archText.split(':')[1]}
                </div>
            </Tab>
            <Tab eventKey="faq" title={t.cloud.overview.tabs.faq}>
                <Accordion className="mt-3">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>{t.cloud.overview.faqQ1}</Accordion.Header>
                    <Accordion.Body>{t.cloud.overview.faqA1}</Accordion.Body>
                  </Accordion.Item>
                </Accordion>
            </Tab>
          </Tabs>
        </Container>
      </section>

      {/* 3️⃣ ✅ SECTION DYNAMIQUE : CATALOGUE AJOUTÉ PAR L'ADMIN (POSTGRESQL) */}
      {dbProducts.length > 0 && (
        <section className="py-5" style={{ background: '#ffffff' }}>
          <Container>
            <div className="text-center mb-5">
              <Badge bg="danger" className="mb-2">{t.cloud.catalog.badge}</Badge>
              <h2 className="fw-bold" dangerouslySetInnerHTML={{ __html: t.cloud.catalog.title }} />
              <p className="text-muted">{t.cloud.catalog.subtitle}</p>
            </div>
            
            <Row className="g-4">
              {dbProducts.map((product) => (
                <Col key={product.id} lg={4} md={6}>
                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                    <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '20px', overflow: 'hidden', borderTop: '4px solid #d90000' }}>
                      <div style={{ height: '200px', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                        <img src={product.image_url || '/images/default-cloud.png'} alt={product.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      </div>
                      <Card.Body className="p-4">
                        <h5 className="fw-bold" style={{ color: '#1e293b' }}>{product.title}</h5>
                        <p className="text-muted small mb-4">{product.description}</p>
                        <div className="pt-3 border-top d-flex justify-content-between align-items-center">
                          <Badge bg="light" text="dark">{t.cloud.catalog.new}</Badge>
                          <Button variant="link" className="p-0 text-danger text-decoration-none fw-bold">
                            {t.cloud.catalog.details} <FaArrowRight size={12} className="ms-1" />
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* 4️⃣ SECTION STATIQUE : AUTRES SOLUTIONS (BAS DE PAGE) */}
      <section
        className="other-solutions-section-sii"
        style={{
          padding: '8rem 0',
          background: `linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(71, 85, 105, 0.95) 100%), url('/images/datacenter2.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <h2 className="text-center text-white fw-bold mb-5" dangerouslySetInnerHTML={{ __html: t.cloud.others.title }} />
          <Row className="g-4">
            <Col lg={4} md={6}>
              <Card className="p-4 text-center border-0 shadow-sm h-100">
                <h4>{t.cloud.others.hci}</h4>
                <p className="small">{t.cloud.others.hciDesc}</p>
              </Card>
            </Col>
            <Col lg={4} md={6}>
              <Card className="p-4 text-center border-0 shadow-sm h-100">
                <h4>{t.cloud.others.backup}</h4>
                <p className="small">{t.cloud.others.backupDesc}</p>
              </Card>
            </Col>
            <Col lg={4} md={6}>
              <Card className="p-4 text-center border-0 shadow-sm h-100">
                <h4>{t.cloud.others.storage}</h4>
                <p className="small">{t.cloud.others.storageDesc}</p>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 5️⃣ SECTION STATIQUE : CTA FINAL */}
      <section className="cta-section-sii py-5 text-center">
        <Container>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} style={{ background: '#1e293b', borderRadius: 24, padding: '3rem', color: 'white' }}>
            <h3 className="fw-bold">{t.cloud.cta.title}</h3>
            <p className="opacity-75">{t.cloud.cta.subtitle}</p>
            <Button variant="danger" size="lg" className="mt-3" onClick={() => window.location.href = '/contact'}>
              <FaPhone className="me-2" /> {t.cloud.cta.btn}
            </Button>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default CloudComputing;